import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import PageHeader from "components/common/PageHeader";
import Paginations from "components/common/Paginations";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showDeleteModal } from "store/slices/modalSlice";
import {
  getVehicleColorList,
  resetFilters,
  setFilter,
  updateVehicleColorStatus,
} from "store/slices/vehicleColorSlice";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";

const VehicleColor = () => {
  const { t } = useTranslation();
  const { trash, edit, noPlans } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("vehicles");
  const {
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    items: vehicleColor,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy },
  } = useSelector((state) => state.vehicleColor);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    const payload = {
      perPage: 50,
      pageNo: page || 1,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getVehicleColorList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        key: "name",
        render: (value) => (
          <div>
            <div>{value?.en}</div>
            {value?.ar && (
              <div className="text-muted small arabic_text">{value.ar}</div>
            )}
          </div>
        ),
      },
      {
        header: t("Color code"),
        key: "code",
        render: (value) => (
          <div className="">
            <span>{value}</span>
            <div
              className="code_box"
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: value,
                borderRadius: "6px",
                border: "1px solid #000",
              }}
            ></div>
          </div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
    ],
    [t]
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              checked={item.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item.status,
                  dispatch,
                  action: updateVehicleColorStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt="edit"
              onClick={() => navigate(`/vehicles/color/edit?id=${item._id}`)}
            />
          </>
        )}
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt={t("delete")}
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Vehicle Color"),
                  message: t(
                    "Are you sure you want to delete this Vehicle Color?"
                  ),
                  itemId: item._id,
                  actionType: "VEHICLE_COLOR",
                })
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, loadingItemId]
  );

  if (!canView) return null;

  return (
    <div className="page-content vehicles_colors_page">
      <PageHeader title={t("Vehicle Color")}/>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy }}
        keywordPlaceholder={t("Search by Name")}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        btnText={t("Add Vehicle Color")}
        onBtnClick={() => navigate("/vehicles/color/add")}
      />

      {loading && <TableShimmer rows={12} columns={6} />}

      {!loading && vehicleColor?.length === 0 && (
        <NoDataFound title={t("No vehicle color found")} icon={noPlans} />
      )}
      {!loading && vehicleColor?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={vehicleColor}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default VehicleColor;
